<script setup lang="ts">
import Container from "~/components/layout/Container.vue";
import Spinner from "~/components/misc/Spinner.vue";
import Product from "~/components/shop/Product.vue";

const config = useRuntimeConfig()
const {params} = useRoute()

const printifyData = ref(null)
const fullyMounted = ref(false)
const selectedSize = ref(null)
const loading = ref(true)
const variantMatch = ref(null)

const sizes = computed(() => {
	if (product.type === 'T Shirt') {
		return {
			"name": "Sizes",
			"type": "size",
			"values": [{"id": 14, "title": "S"}, {"id": 15, "title": "M"}, {"id": 16, "title": "L"}, {
				"id": 17,
				"title": "XL"
			}, {"id": 18, "title": "2XL"}],
			"display_in_preview": false
		};
	}

	if (!printifyData) {
		return []
	}

	if (!printifyData.value) {
		return []
	}

	if (!printifyData.value.options.length) {
		return []
	}

	return printifyData.value.options[1]
})

const {product} = await GqlGetProduct({
	slug: params.slug
})

let youtube = null
let preOrder = false
if (product) {
	product.properties.forEach(property => {
		if (property.key.includes("Pre-Order")) {
			preOrder = true
		}
    if (property.key.includes("YouTube")) {
      youtube = property.value
    }
	})
}

const { offer } = await GqlGetOffer({
	productId: product.productId
})

const { variants } = await GqlGetVariant({
	slug: product.productId
})

const offersResponse = await GqlGetOffers();
const offers = offersResponse.offers;

const route = useRoute()

const hasDiscountPrice = function() {
    return !!offer.discountPrice
}

const getDiscountPrice = function() {
    if (!hasDiscountPrice()) {
        return 0
    }

    if (country.value == "RO") {
        return `${(offer.discountPrice * 5.2 / 100).toFixed(0)} RON`
    }
    return `€${(offer.discountPrice / 100).toFixed(2)}`
}

const albumSchema = {
  "@context": "https://schema.org",
  "@type": ["Product", "MusicAlbum"],
  "name": product?.title,
  "image": product?.image?.url,
  "description": product?.description?.text,
  "sku": product?.productId,
  "offers": {
    "@type": "Offer",
    "url": 'https://neuronoir.art' + route.path,
    "priceCurrency": "EUR",
    "price": hasDiscountPrice() ? (offer?.discountPrice / 100).toFixed(2) : (offer?.price / 100).toFixed(2),
    "availability": "https://schema.org/InStock"
  },
}

useHead({
	title: `${product?.title} | Neuronoir Shop`,
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(albumSchema)
    }
  ],
	meta: [
		{ name: 'description', content: product?.description?.text },
		{ property: 'og:title', content: `${product?.title} | Neuronoir Shop` },
		{ property: 'og:description', content: product?.description?.text },
    { property: 'canonical', content: 'https://neuronoir.art' + route.path },
    { property: 'og:image', content: product?.image?.url },
	]
})

if (product.type === 'T-Shirt') {
	fetch(config.public.baseURL + "/api/products/" + product.productId).then(response => {
		response.json().then(data => {
			printifyData.value = data
			fullyMounted.value = true

			if (sizes) {
				sizes.value.values.forEach(option => {
					if (option.title === 'M') {
						selectSize(option)
					}
				})
			}

			loading.value = false
		})
	})
} else {
	loading.value = false
	fullyMounted.value = true

	if (!Array.isArray(sizes.value)) {
		sizes.value.values.forEach(option => {
			if (option.title === 'M') {
				selectSize(option)
			}
		})
	}
}

function getEmagRomania() {
  if (!product?.listing.length) {
    return "";
  }

  const listings = product.listing.filter(list => list.marketplaceName === "eMAG Romania")
  if (!listings.length) {
    return "";
  }

  return listings[0].url;
}

function getDiscogs() {
  if (!product?.listing.length) {
    return "";
  }

  const listings = product.listing.filter(list => list.marketplaceName === "Discogs")
  if (!listings.length) {
    return "";
  }

  return listings[0].url;
}

function addToCart() {
	if (process.client) {
		const cartData = localStorage.getItem("neuronoirCart")
		const item = {
			'product': product,
			'selectedSize': selectedSize.value,
			'quantity': 1
		}

		let cart = []
		if (cartData) {
			cart = JSON.parse(cartData)
			cart.push(item)
		} else {
			cart = [item]
		}

		localStorage.setItem('neuronoirCart', JSON.stringify(cart))
		window.location.href = '/cart'
	}
}

const country = useState('country')

function getPrice(entity) {
    if (country.value == 'RO') {
        return `${(entity.price * 5.2 / 100).toFixed(0) } RON`
    }

    return `€${(entity.price / 100).toFixed(2) }`
}

function selectSize(size) {
	selectedSize.value = size

	if (product?.type === 'T-Shirt') {
		const match = printifyData.value.variants.filter(variant => {
			if (!variant.is_enabled) {
				return false
			}

			let variantWithSize = variant.options.filter(option => {
				return option === size.id
			})

			return !!variantWithSize.length
		})

		variantMatch.value = match[0]
	}

	if (product?.type === 'T Shirt') {
		let match = variants.filter(variant => {
			if (variant.variant == size.title) {
				return true
			}

			return false
		})

		if (match[0].stock <= 0) {
			match[0].is_available = false
		} else {
			match[0].is_available = true
		}

		variantMatch.value = match[0]
	}
}
</script>

<template>
    <div>
        <Container class="bg-neurodarkgreyvar">
            <div class="grid lg:grid-cols-2 grid-cols-1 gap-10 py-10" v-if="product">
                <div>
                    <nuxt-link to="/shop" class="text-neurolink mb-5 block">Back to shop</nuxt-link>
                    <h1 class="text-4xl font-bold text-white block">{{ product.title }}</h1>
                    <p class="text-white font-light opacity-50 block mb-5">{{ product.type }}</p>
                    <nuxt-img :alt="product.title" :src="product.image.url" class="w-full" />
                </div>
                <div class="mt-20">
                    <Spinner v-if="loading"></Spinner>
                    <div v-if="variantMatch">
                        <div class="text-red-700" v-if="!variantMatch.is_available">The selected size is currently out of
                            stock.
                        </div>
                    </div>
                    <div v-if="fullyMounted && (product.type === 'T-Shirt' || product.type === 'T Shirt')">
                        <div class="text-white font-light opacity-50 pb-3 mt-10">Size</div>
                        <span v-for="option in sizes.values"
                              :class="selectedSize?.title === option.title ? 'border border-white bg-neuroprimary rounded border-opacity-50 px-3 py-2 mr-3' : 'border border-white rounded border-opacity-50 px-3 py-2 mr-3'">
						<button @click="selectSize(option)" class="mx-auto my-auto text-white font-bold">
							{{ option.title }}
						</button>
					</span>
                    </div>
                    <div class="grid grid-cols-3 gap-5 max-sm:pt-0 pt-10">
                        <div class="col-span-2">
                            <button @click="addToCart"
                                    class="w-full h-full border border-neuroprimary bg-neuroprimary py-2 rounded text-white"
                                    v-if="offer.stock > 0 && !preOrder">
                                Add to cart
                            </button>
                            <button @click="addToCart"
                                    class="w-full border border-neuroprimary bg-neuroprimary py-2 rounded text-white"
                                    v-if="offer.stock > 0 && preOrder">
                                Pre-Order
                            </button>
                            <span class="block text-center w-full border border-error bg-error py-2 rounded text-white"
                                  v-if="offer.stock < 1">
                                Currently not in stock
                            </span>
                        </div>
                        <div class="col-span-1" v-if="variantMatch">
                            <div
                                class="border text-center border-white border-opacity-20 py-2 rounded text-white font-bold">
                                {{getPrice(variantMatch)}}
                            </div>
                        </div>
                        <div class="col-span-1" v-if="product.type != 'T-Shirt' && product.type != 'T Shirt'">
                            <div v-if="!hasDiscountPrice()"
                                class="border text-center border-white border-opacity-20 py-2 rounded text-white font-bold">
                                {{getPrice(offer)}}
                            </div>
                            <div v-if="hasDiscountPrice()"
                                 class="border text-center border-white border-opacity-20 py-2 rounded text-white ">
                                <span class="line-through opacity-50 max-sm:block inline-block">{{getPrice(offer)}}</span>
                                <span class="font-bold px-1 max-sm:block inline-block">{{getDiscountPrice(offer)}}</span>
                            </div>
                        </div>
                    </div>
                    <div class="flex gap-5 my-5 text-center mx-auto">
                      <img src="/visa.svg" class="w-10"/>
                      <img src="/mc.svg" class="w-10"/>
                      <img src="/google_pay.svg" class="w-10"/>
                      <img src="/apple_pay.svg" class="w-10"/>
                      <img src="/revolut_payment_method.svg" class="w-10"/>
                    </div>
                    <div class="my-10 border border-b w-full border-white border-opacity-10"></div>

                  <a :href="getEmagRomania()" target="_blank" class="block text-center w-full border border-white bg-white py-2 rounded" v-if="getEmagRomania()">
                    <span class="bg-gradient-to-r from-red-500 to-blue-500 inline-block text-transparent bg-clip-text">View on eMAG Romania</span>
                  </a>

                  <a :href="getDiscogs()" target="_blank" class="block text-center w-full border border-white bg-white py-2 rounded my-2" v-if="getDiscogs()">
                    <span class="text-black">View on Discogs</span>
                  </a>

                    <div class="grid max-lg:grid-cols-2 grid-cols-3 gap-5 py-5">
                        <div v-for="property in product.properties">
                            <div v-if="property.visible">
                                <div class="text-white font-light opacity-50">{{ property.key }}</div>
                                <span class="text-white">{{ property.value }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-10 pb-20 pt-10">
                        <div>
						<span class="px-2 py-2 bg-white bg-opacity-5 rounded-xl">
							<Icon class="mb-1" name="iconoir:delivery-truck" color="white" />
						</span>
                            <span class="my-auto pl-2 text-white font-light">2-15 days shipping</span>
                        </div>
                        <div>
						<span class="px-2 py-2 bg-white bg-opacity-5 rounded-xl">
							<Icon class="mb-1" name="iconoir:money-square" color="white" />
						</span>
                            <span class="my-auto pl-2 text-white font-light">VAT included</span>
                        </div>
                        <div v-if="offer.stock < 20 && offer.stock > 0">
						<span class="px-2 py-2 bg-red-900 rounded-xl">
							<Icon class="mb-1" name="iconoir:garage" color="white" />
						</span>
                            <span class="my-auto pl-2 text-red-500 font-light">Only a few more left!</span>
                        </div>
                    </div>

                    <div v-if="product.releases.length" class="text-white font-light opacity-50 mb-3">Releases</div>
                    <div v-if="product.releases.length" class=" grid max-lg:grid-cols-1 grid-cols-2">
                        <nuxt-link :to="`/releases/${release.slug}`" class="flex my-3 col-span-1" v-for="release in product.releases">
                            <nuxt-img :alt="release.name" :src="release.image.url" class="w-16 h-16" />
                            <div class="my-auto pl-3">
                                <div class="uppercase font-bold text-white">
                                    {{release.artist.name}}
                                    <Icon class="mb-1" name="iconoir:link" color="white" />
                                </div>
                                <div class="text-white font-light opacity-50">{{release.name}}</div>
                            </div>
                        </nuxt-link>
                    </div>
                </div>
            </div>
          <div v-if="youtube">
            <iframe class="w-full aspect-video" :src="youtube"></iframe>
          </div>
            <div class="py-10" v-if="product">
                <h5 class="pb-3 text-neurolightgrey">Description</h5>
                <div class="text-white htmlblock" v-html="product.description.html"></div>
            </div>
        </Container>
	    <Container class="bg-neurodarkgrey">
		    <div class="py-10 text-neurolightgrey">Similar products</div>
		    <div class="grid xl:grid-cols-3 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-3 pb-20">
			    <div class="col-span-1" v-for="similar, i in product.similarProducts" :key="i" v-if="offers">
				    <Product
					    :offers="offers"
					    :title="similar.title"
					    :type="similar.type"
					    :image-url="similar.image.url"
					    :brand="similar.brand"
					    :label="similar.label"
					    :slug="similar.slug"
					    :subtype="similar.subtype"
					    :productId="similar.productId"
				    ></Product>
			    </div>
		    </div>
	    </Container>
        <Container class="bg-neurodarkgreyvar" v-if="product.artists.length">
            <div class="w-full py-12" v-for="artist in product.artists">
                <nuxt-link :to="`/artists/${artist.slug}`" class="text-neurolink block pb-12">More about the artist</nuxt-link>
                <PostType :type="artist.genre" class="mb-3"></PostType>
                <nuxt-img :alt="artist.name" :src="artist.profileImage.url" class="lg:float-right w-60 rounded" />
                <h5 class="text-white font-bold text-4xl max-lg:pt-5 uppercase">{{artist.name}}</h5>
                <p class="text-white opacity-50 pt-10">{{artist.description.markdown}}</p>
                <div class="pt-5">
                    <a :href="social.url" class="text-neurolink mr-2" v-for="social in artist.links">
                        <Icon :name="social.icon" size="22px"/>
                    </a>
                </div>
            </div>
        </Container>
    </div>
</template>

<style>
.htmlblock p {
	padding-bottom: 5px !important;
}

.htmlblock ul {
	list-style: circle;
}
</style>
